/* eslint-disable max-lines */
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { I18NextService } from 'angular-i18next';
import { TypedFormControl } from 'core';
import { addMinutes, parseISO } from 'date-fns';
import { SPECIAL_REGIONS_WITHOUT_CITY_NAME } from './global-constants';
import { TypedFormGroup } from './typed-form-group';

export const INPUT_DEBOUNCE_TIME = 300;

export function isServerValidationError(error: HttpErrorResponse): boolean {
    return error && error.status === 400;
}

export function stringMatches(value: string | null, searchTerm: string): boolean {
    return !!value && value.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;
}

/* eslint-disable max-len */
export function getDropdownSearchTitle(searchTerm: string, minimalInputLengthForSearch: number, i18next: I18NextService): string {
    const length = !!searchTerm ? searchTerm.length : 0;
    if (minimalInputLengthForSearch - length === 1) {
        return i18next.t('shared:ngSelect.typeToSearchTextOneMoreSymbol');
    }
    return length >= minimalInputLengthForSearch
        ? i18next.t('shared:ngSelect.notFoundText')
        : i18next.t('shared:ngSelect.typeToSearchTextWithLength', { length: minimalInputLengthForSearch - length });
}

/**
 * Данная функция используется в сочетании с RxJS-оператором distinctUntilChanged.
 * Реализация сравнения по-умолчании в distinctUntilChanged считает что null и null не равны между собой,
 * что, в общем случае, является верной логикой. Но на примитивных полях форм может приводить к лишним срабатываниям триггеров
 * При этом, в реактивных формах angular нет внятного способа получить текущее и предыдущее значение контрола (см. {@link https://stackoverflow.com/questions/44898010/form-control-valuechanges-gives-the-previous-value | stackoverflow } и связанный {@link https://github.com/angular/angular/issues/13129 | github issue })
 */
/* eslint-enable max-len */
export function nullsAreEqualValuesComparer(x: any, y: any): boolean {
    if (x === null && y === null) {
        return true;
    }
    return x === y;
}

export function collectionsAreIdentical(x: any[], y: any[]): boolean {
    if (!x || !y) {
        return false;
    }
    if (x.length !== y.length) {
        return false;
    }
    return x.every((item, index) => item === y[index]);
}

export function distinctBy<T>(comparerFn: (left: T, right: T) => boolean): (item: T, index: number, allItems: T[]) => boolean {
    return function(item: T, index: number, allItems: T[]): boolean {
        return index === allItems.findIndex(x => comparerFn(item, x));
    };
}

export function getDefaultTraits(): Backend.EditTraitsState {
    return {
        canBeCancelledByCurrentUser: false,
        canBeCopiedByCurrentUser: false,
        canBeEditedByCurrentUser: true,
        canBeForceCancelledByCurrentUser: false,
        currentUserCanApplyCarrierEdit: false
    };
}
export function getEmptyRequestState(businessProcessType: BusinessProcessType, agencyInfo: Backend.AgencyShortInfo = ETR.agencyInfo): CanonicalStateWithTraits {
    return {
        canonicalInfo: {
            agency: agencyInfo
                ? {
                    code: agencyInfo.code,
                    hasValidDirectAgreement: agencyInfo.hasValidDirectAgreement,
                    hasValidBSPAgreement: agencyInfo.hasValidBSPAgreement,
                    hasValidBlockAgreement: agencyInfo.hasValidBlockAgreement,
                    hasValidCharterAgreement: agencyInfo.hasValidCharterAgreement,
                    hasAuthorizedOffices: agencyInfo.hasAuthorizedOffices,
                    isResident: agencyInfo.isResident,
                    name: agencyInfo.name,
                    nameTranslation: agencyInfo.nameTranslation,
                    partnerFileId: agencyInfo.partnerFileId,
                    saleCurrencyCode: agencyInfo.saleCurrencyCode,
                    reportCurrencyCode: agencyInfo.reportCurrencyCode,
                    agencySource: agencyInfo.agencySource
                  }
                : null,
            businessProcessType,
            copiedRequestCorrelationId: null,
            copiedRequestNumber: null,
            correlationId: null,
            createdByImpersonatedRM: false,
            createdAt: null,
            creatorDisplayName: null,
            creatorEmail: null,
            creatorLogin: null,
            isTestRequest: null,
            postSentInfo: {
                completedAt: null,
                completedBy: null,
                completionComment: null,
                currentStatus: 'NotSent',
                latestCarrierEditCreatedAt: null,
                requestWorkflowStages: []
            },
            requestNumber: null,
            rowVersion: null,
            senderDisplayName: null,
            senderEmail: null,
            senderLogin: null,
            sentAt: null,
            createdByCorpClientWithCode: null,
            createdByImpersonatedAsCorpClient: null
        },
        editTraits: getDefaultTraits()
    };
}

export function booleanToLocaleString(i18next: I18NextService, value: boolean): string {
    if (value) {
        return i18next.t('shared:labels.yes');
    } else {
        return i18next.t('shared:labels.no');
    }
}

export function getFormControlPath(control: AbstractControl | null): string | null {
    let controlName = null;
    const parent = control?.parent;
    if (parent instanceof UntypedFormGroup) {
        Object.keys(parent.controls).forEach(name => {
            if (control === parent.controls[name]) {
                controlName = name;
            }
        });
    }
    if (parent instanceof UntypedFormArray) {
        Object.keys(parent.controls).forEach(name => {
            if (control === parent.controls[name]) {
                controlName = `[${name}]`;
            }
        });
    }
    if (parent && parent.parent) {
        const parentName = getFormControlPath(parent);
        if (parentName) {
            controlName = `${parentName}.${controlName}`;
        }
    }
    return controlName;
}
export function scrollToFirstInvalidControl(): void {
    setTimeout(() => {
        /*
        выставляем таймаут, чтобы сообщения валидации успели отрендериться
         */
        const header = document.querySelector('.header');
        const headerHeight = !!header ? header.clientHeight : 0;
        const errorControl = document.querySelector('.s7-form-error');
        if (errorControl !== null) {
            const offsetFromControlWithError = 70;
            const y = errorControl.getBoundingClientRect().top;
            // https://stackoverflow.com/questions/2717252/document-body-scrolltop-is-always-0-in-ie-even-when-scrolling/2717261
            const docScrollTop = document.documentElement.scrollTop || document.body.scrollTop;
            window.scroll({ left: 0, top: docScrollTop + y - headerHeight - offsetFromControlWithError, behavior: 'smooth' });
        }
    }, 0);
}
export function getFilesFromDataTransfer(event: DragEvent): File[] {
    const files: File[] = [];
    if (event.dataTransfer.items) {
        /* eslint-disable-next-line @typescript-eslint/prefer-for-of */
        for (let i = 0; i < event.dataTransfer.items.length; i++) {
            if (event.dataTransfer.items[i].kind === 'file') {
                files.push(event.dataTransfer.items[i].getAsFile());
            }
        }
        event.dataTransfer.items.clear();
    } else {
        /* eslint-disable-next-line @typescript-eslint/prefer-for-of */
        for (let i = 0; i < event.dataTransfer.files.length; i++) {
            files.push(event.dataTransfer.files[i]);
        }
        event.dataTransfer.clearData();
    }

    return files;
}

export function trimStartSiteUrl(webSiteAddress: string): string {
    if (webSiteAddress === null) {
        return null;
    }
    return webSiteAddress.replace(new RegExp('^\\s*(https?://)?(www\\.)?', 'i'), '');
}

export function trimSiteUrl(webSiteAddress: string): string {
    if (webSiteAddress === null) {
        return null;
    }
    return trimStartSiteUrl(webSiteAddress).replace(new RegExp('[\\.\\/]+$', 'i'), '');
}

export const DATEFORMAT = {
    dateRu: 'dd.MM.yyyy',
    dateEn: 'MM/dd/yyyy',
    dateLongRu: 'dd MMMM yyyy',
    dateLongEn: 'MMMM dd, yyyy',
    dateWithTimeRu: 'dd.MM.yyyy, HH:mm',
    dateWithTimeEn: 'MM/dd/yyyy, h:mm a',
    dateWithTimeAndSecondsRu: 'dd.MM.yyyy, HH:mm:ss',
    dateWithTimeAndSecondsEn: 'MM/dd/yyyy, h:mm:ss a',
    time: 'HH:mm',
    isoDateTime: 'yyyy-MM-ddTHH:mm:ssX',
    isoShortDate: 'yyyy-MM-dd'
};

// https://learn.javascript.ru/blob
export function saveBlobResponseAsFile(resp: HttpResponse<Blob>, customFileName: string = null): void {
    const fileName = customFileName ? customFileName : decodeURI(resp.headers.get('content-disposition').substring('attachment; filename='.length));
    if (!!window.navigator.msSaveBlob) {
        window.navigator.msSaveBlob(resp.body, fileName);
    } else {
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(resp.body);
        // Фикс для Safari, ибо документ не успевает прогрузиться до момента удаления тега <a href="url"></a>
        const ms = navigator.userAgent.indexOf('Safari') > -1 ? 1000 : 0;
        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        setTimeout(function(): void {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }, ms);
    }
}

export function downloadFileViaHttpClient(http: HttpClient, url: string, notFoundErrorMessage: string, displayError: (message: string) => void): void {
    http.get(url, { observe: 'response', responseType: 'blob' as 'json' }).subscribe(
        (resp: HttpResponse<Blob>) => {
            saveBlobResponseAsFile(resp)
        },
        (errorResponse: HttpErrorResponse) => {
            if (errorResponse.status === 404) {
                displayError(notFoundErrorMessage);
            } else {
                displayError(errorResponse.message);
            }
        }
    );
}

export function downloadFilePostHttpClient(
    http: HttpClient,
    url: string,
    model: any,
    notFoundErrorMessage: string,
    displayError: (message: string) => void
): void {
    http.post(url, model, { observe: 'response', responseType: 'blob' as 'json' }).subscribe(saveBlobResponseAsFile, (errorResponse: HttpErrorResponse) => {
        if (errorResponse.status === 404) {
            displayError(notFoundErrorMessage);
        } else {
            displayError(errorResponse.message);
        }
    });
}

export function returnNoneIfNull(value: any): any {
    return value === null ? 'None' : value;
}
export function disableEnter(event: KeyboardEvent): boolean {
    if (event.key === 'Enter') {
        event.preventDefault();
        return false;
    }
    return true;
}

export function validateFormWithDescendants(form: TypedFormGroup<any>): void {
    Object.values(form.controls).forEach((form: TypedFormGroup<any> | TypedFormControl<any>) => {
        if ('controls' in form) {
            validateFormWithDescendants(form);
        } else {
            if (form.value === null) {
                form.markAsDirty();
                form.updateValueAndValidity();
            }
        }
    });
}

// Вспомогательные функции для тестов
export function setValueAndhaveValidationAndShowErrorMessage(form: TypedFormGroup<unknown>, key: string, value: any): AbstractControl {
    const ctrl = form.get(key);
    ctrl.setValue(value);
    ctrl.updateValueAndValidity();
    ctrl.markAsDirty();
    return ctrl;
}
export function getAllFormControlPathsWithSubGroups(control: UntypedFormGroup): string[] {
    const pathsArray: string[] = [];

    function getPathsOfControl(currControl: UntypedFormGroup | UntypedFormArray, currPath: string): void {
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const key in currControl.controls) {
            const childControl = currControl.controls[key]
            if (childControl instanceof UntypedFormControl) {
                pathsArray.push(currPath + key)
            }
            if (childControl instanceof UntypedFormGroup || childControl instanceof UntypedFormArray) {
                getPathsOfControl(childControl, `${currPath}${key}.`)
            }
        }
    }

    getPathsOfControl(control, '')

    return pathsArray
}

export function timeToUtc(time: string | null): string {
    return !time ? null : addMinutes(
        parseISO(time),
        -new Date().getTimezoneOffset()
    ).toISOString();
}

export function timeToLocalTime(time: string | null): string {
    if (!time) {
        return null;
    }

    const isoTime = parseISO(time);

    return addMinutes(isoTime, isoTime.getTimezoneOffset()).toISOString();
}

/**
 * Возвращает смещение часового пояся в UTC
 *
 * @param date - дата относительно которой определяется смещение
 * @returns смещение часового пояса (Например: +7, -2, +0)
 */
export function getUTCOffset(date: Date): string {
    const offsetMinutes = date.getTimezoneOffset();
    const offsetHours = -offsetMinutes / 60;
    const sign = offsetHours >= 0 ? '+' : '-';
    const absOffset = Math.abs(offsetHours);
    return `${sign}${absOffset}`;
}

export function getAddressString(address: Backend.TaxpayerCompanyAddress): string {
    const addressArray = [];

    if (address.index) {
        addressArray.push(address.index);
    }

    if (address.regionName) {
        addressArray.push(`${address.regionType ?? ''} ${address.regionName}`.trim())
    }

    if (!SPECIAL_REGIONS_WITHOUT_CITY_NAME.includes(address.regionName)) {
        if (address.cityName) {
            addressArray.push(`${address.cityType ?? ''} ${address.cityName}`.trim());
        }
    }

    if (address.streetName) {
        addressArray.push(`${address.streetType ?? '' } ${address.streetName}`.trim())
    }
    if (address.house) {
        addressArray.push(address.house)
    }

    if (address.flat) {
        addressArray.push(address.flat)
    }

    return addressArray.join(', ');
}
export function getFirstValue<T>(array: T[]): T | null {
    return array?.length ? array[0] : null
}

export function extractTimezoneFromISOString(originalValue: Date | string | number): string {
    if (typeof originalValue === 'string') {
        // Проверяем, является ли строка UTC (заканчивается на Z или +00:00)
        if (originalValue.toUpperCase().endsWith('Z') ||
            originalValue.includes('+00:00') ||
            originalValue.includes('+0000')) {
            return 'UTC';
        }

        // Ищем смещение в формате +HH:MM или -HH:MM
        const timezoneMatch = originalValue.match(/[+-]\d{2}:?\d{2}$/);
        if (timezoneMatch) {
            const offset = timezoneMatch[0].replace(':', '');
            const hours = parseInt(offset, 10);
            return `UTC${hours >= 0 ? '+' : ''}${hours}`;
        }
    }

    // Для Date объекта - используем смещение браузера
    if (originalValue instanceof Date) {
        const timezoneOffset = originalValue.getTimezoneOffset();
        const timezoneHours = -timezoneOffset / 60;

        if (timezoneHours === 0) {
            return 'UTC';
        } else {
            const sign = timezoneHours >= 0 ? '+' : '';
            return `UTC${sign}${timezoneHours}`;
        }
    }

    return 'UTC';
}

